<template>
  <div class="facility-dashboard">
    <div class="card shadow-sm table-card mb-4">
      <div class="form-card-header flex-wrap">
        <div class="section-number"><i class="fe fe-activity" style="font-size: 12px;"></i></div>
        <div class="flex-grow-1">
          <h6 class="mb-0 font-weight-bold text-white">Facility performance</h6>
          <small style="color: rgba(255,255,255,.75);">Utilization, authorization codes, referrals, claims and ratings{{ data ? ` · ${data.period.from} to ${data.period.to}` : "" }}</small>
        </div>
        <div class="btn-group btn-group-sm" role="group" aria-label="View">
          <button type="button" class="btn" :class="mode === 'count' ? 'btn-light' : 'btn-outline-light'" @click="mode = 'count'">Counts</button>
          <button type="button" class="btn" :class="mode === 'rate' ? 'btn-light' : 'btn-outline-light'" @click="mode = 'rate'">Per enrollee</button>
        </div>
      </div>
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
          <PeriodPicker v-model="period" @change="loadAll" default-preset="90d" />
          <select class="form-control form-control-sm" style="width: auto;" v-model="lgaId" @change="loadAll" aria-label="LGA">
            <option :value="null">All LGAs</option>
            <option v-for="l in lgas" :key="l.id" :value="l.id">{{ l.local_name }}</option>
          </select>
          <input class="form-control form-control-sm" style="max-width: 200px;" placeholder="Search facility…" v-model="search" @keyup.enter="loadAll" />
        </div>

        <div v-if="loading && !data" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
        <template v-else-if="data">
          <div class="row">
            <div class="col-lg col-md-4 col-6" v-for="tile in tiles" :key="tile.label">
              <div class="stat-card">
                <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
                <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub">{{ tile.sub }}</p></div>
              </div>
            </div>
          </div>

          <!-- Leaderboard -->
          <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
            <h6 class="font-weight-bold mb-0">Top facilities by</h6>
            <select class="form-control form-control-sm" style="width: auto;" v-model="metric" aria-label="Measure">
              <option v-for="m in metricOptions" :key="m.key" :value="m.key">{{ m.label }}</option>
            </select>
          </div>
          <apexchart type="bar" :height="Math.max(220, leaderboard.length * 28)" :options="leaderOptions" :series="leaderSeries" :key="'lead' + chartKey + metric" />
          <p class="small text-muted">{{ metricHint }}</p>
        </template>
      </div>
    </div>

    <div class="row" v-if="data">
      <!-- Trend -->
      <div class="col-lg-7">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-trending-up" style="font-size: 12px;"></i></div>
            <h6 class="mb-0 font-weight-bold text-white">Activity over time (all facilities)</h6>
          </div>
          <div class="card-body"><apexchart v-if="trend" type="line" height="300" :options="trendOptions" :series="trendSeries" :key="'trend' + chartKey" /></div>
        </div>
      </div>
      <!-- Diagnoses -->
      <div class="col-lg-5">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-thermometer" style="font-size: 12px;"></i></div>
            <div class="flex-grow-1">
              <h6 class="mb-0 font-weight-bold text-white">{{ dx ? `Facilities seeing ${dx.diagnosis.name}` : "Top diagnoses" }}</h6>
              <small style="color: rgba(255,255,255,.75);">{{ dx ? "Encounters and rate per 1,000 enrollees" : "Click a diagnosis to see which facilities treat it" }}</small>
            </div>
            <button v-if="dx" class="btn btn-sm btn-light" @click="dx = null"><i class="fe fe-arrow-left"></i></button>
          </div>
          <div class="card-body">
            <apexchart v-if="!dx && diagnoses && diagnoses.diagnoses.length" type="bar" height="300" :options="dxOptions" :series="dxSeries" :key="'dx' + chartKey" />
            <div v-else-if="!dx" class="empty-state">No encounters with a diagnosis in this period.</div>
            <div v-else class="table-responsive" style="max-height: 300px; overflow-y: auto;">
              <table class="table table-sm mb-0">
                <thead class="table-head"><tr><th class="table-th">Facility</th><th class="table-th text-right">Encounters</th><th class="table-th text-right">Per 1,000</th></tr></thead>
                <tbody>
                  <tr class="table-row" v-for="f in dx.facilities" :key="f.facility_id">
                    <td><router-link :to="`/provider-${f.facility_id}`">{{ f.name }}</router-link></td>
                    <td class="text-right">{{ f.encounters }}</td><td class="text-right">{{ f.per_1000 === null ? "—" : f.per_1000 }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ranking table -->
    <div class="card shadow-sm table-card mb-4" v-if="data">
      <div class="form-card-header">
        <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
        <div class="flex-grow-1">
          <h6 class="mb-0 font-weight-bold text-white">Facility ranking</h6>
          <small style="color: rgba(255,255,255,.75);">Click a column to rank by it; click a facility for its own dashboard</small>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table mb-0">
          <thead class="table-head">
            <tr>
              <th class="table-th">#</th>
              <th class="table-th">Facility</th>
              <th v-for="c in columns" :key="c.key" class="table-th text-right sortable" @click="sortBy(c.key)" :title="c.title">
                {{ c.label }} <i v-if="sort === c.key" class="fe" :class="direction === 'desc' ? 'fe-arrow-down' : 'fe-arrow-up'"></i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="table-row" v-for="(r, i) in sortedRows" :key="r.facility_id">
              <td class="text-muted">{{ i + 1 }}</td>
              <td><router-link :to="`/provider-${r.facility_id}`"><b>{{ r.name }}</b></router-link><div class="small text-muted">{{ r.lga || "—" }}</div></td>
              <td v-for="c in columns" :key="c.key" class="text-right">{{ c.format ? c.format(r[c.key], r) : (r[c.key] === null ? "—" : Number(r[c.key]).toLocaleString()) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import PeriodPicker from "./PeriodPicker.vue";

/**
 * Facilities dashboard for the facilities page (GET analytics/facilities, /trend, /diagnoses): leaderboard,
 * ranking table, counts or rates per assigned enrollee, diagnosis drill-down, ratings. All for a period.
 */
export default {
  name: "FacilityDashboard",
  components: { PeriodPicker },
  data() {
    const agency = JSON.parse(localStorage.getItem("agency") || "null") || {};
    return {
      agency,
      period: { from: "", to: "" },
      lgaId: null,
      lgas: [],
      search: "",
      mode: "count",
      metric: "encounters",
      sort: "encounters",
      direction: "desc",
      data: null,
      trend: null,
      diagnoses: null,
      dx: null,
      loading: false,
      chartKey: 0,
    };
  },
  computed: {
    rows() { return this.data ? this.data.rows : []; },
    t() { return this.data.totals; },
    money() { return (v) => "₦" + Math.round(v || 0).toLocaleString(); },
    tiles() {
      const t = this.t;
      return [
        { label: "Facilities with encounters", value: `${t.facilities_with_encounters} / ${this.data.facility_count}`, sub: `${t.enrollees.toLocaleString()} enrollees assigned`, icon: "fe fe-home", bg: "#eff6ff", color: "#2563eb" },
        { label: "Encounters", value: t.encounters.toLocaleString(), sub: t.encounters_per_1000 === null ? "" : `${t.encounters_per_1000} per 1,000 enrollees`, icon: "fe fe-activity", bg: "#ecfdf5", color: "#059669" },
        { label: "Authorization codes", value: t.codes_requested.toLocaleString(), sub: `${t.codes_approved.toLocaleString()} approved`, icon: "fe fe-shield", bg: "#f5f3ff", color: "#7c3aed" },
        { label: "Referrals", value: `${t.referrals_out.toLocaleString()} out`, sub: `${t.referrals_in.toLocaleString()} in, ${t.referrals_in_taken_up.toLocaleString()} taken up`, icon: "fe fe-share-2", bg: "#fff7ed", color: "#ea580c" },
        { label: "Claims", value: t.claims_submitted.toLocaleString(), sub: `${this.money(t.amount_approved)} approved of ${this.money(t.amount_claimed)}`, icon: "fe fe-file-text", bg: "#fdf2f8", color: "#db2777" },
        { label: "Average rating", value: t.rating === null ? "—" : `${t.rating} ★`, sub: `${t.ratings_count.toLocaleString()} ratings`, icon: "fe fe-star", bg: "#fefce8", color: "#ca8a04" },
      ];
    },
    metricOptions() {
      return this.mode === "rate"
        ? [
          { key: "encounters_per_1000", label: "Encounters per 1,000 enrollees" },
          { key: "patients_seen_pct", label: "% of enrollees seen" },
          { key: "claims_per_1000", label: "Claims per 1,000 enrollees" },
          { key: "code_approval_pct", label: "% of codes approved" },
          { key: "referrals_out_per_1000_encounters", label: "Referrals out per 1,000 encounters" },
          { key: "referral_uptake_pct", label: "% of incoming referrals taken up" },
          { key: "rating", label: "Average rating" },
        ]
        : [
          { key: "encounters", label: "Encounters" },
          { key: "enrollees", label: "Enrollees assigned" },
          { key: "codes_requested", label: "Authorization codes requested" },
          { key: "referrals_out", label: "Referrals out" },
          { key: "referrals_in", label: "Referrals in" },
          { key: "claims_submitted", label: "Claims submitted" },
          { key: "amount_claimed", label: "Amount claimed" },
          { key: "amount_approved", label: "Amount approved" },
          { key: "ratings_count", label: "Number of ratings" },
        ];
    },
    metricHint() {
      return this.mode === "rate"
        ? "Rates put each facility in context of the enrollees assigned to it (principals and dependants), so small and large facilities compare fairly."
        : "Raw volumes for the period. Switch to “Per enrollee” to compare facilities of different sizes.";
    },
    leaderboard() {
      return this.rows.filter((r) => r[this.metric] !== null && r[this.metric] > 0)
        .sort((a, b) => b[this.metric] - a[this.metric]).slice(0, 15);
    },
    leaderSeries() {
      return [{ name: this.metricOptions.find((m) => m.key === this.metric)?.label || this.metric, data: this.leaderboard.map((r) => r[this.metric]) }];
    },
    leaderOptions() {
      const rows = this.leaderboard;
      const isMoney = ["amount_claimed", "amount_approved"].includes(this.metric);
      return {
        chart: { toolbar: { show: true }, events: { dataPointSelection: (e, ctx, cfg) => rows[cfg.dataPointIndex] && this.$router.push(`/provider-${rows[cfg.dataPointIndex].facility_id}`) } },
        colors: [this.mode === "rate" ? "#7c3aed" : "#2563eb"],
        plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 3, distributed: false } },
        xaxis: { categories: rows.map((r) => r.name), labels: { formatter: (v) => (isMoney ? "₦" : "") + Math.round(v).toLocaleString() } },
        dataLabels: { enabled: true, formatter: (v) => (isMoney ? "₦" : "") + Number(v).toLocaleString(), style: { fontSize: "11px" } },
        tooltip: { y: { formatter: (v) => (isMoney ? "₦" : "") + Number(v).toLocaleString() } },
      };
    },
    columns() {
      const pct = (v) => (v === null ? "—" : v + "%");
      const star = (v, r) => (v === null ? "—" : `${v} ★ (${r.ratings_count})`);
      return this.mode === "rate"
        ? [
          { key: "enrollees", label: "Enrollees" },
          { key: "encounters_per_1000", label: "Enc./1,000", title: "Encounters per 1,000 enrollees" },
          { key: "patients_seen_pct", label: "Seen", format: pct, title: "% of enrollees seen at least once" },
          { key: "code_approval_pct", label: "Codes approved", format: pct },
          { key: "referrals_out_per_1000_encounters", label: "Refer out/1,000 enc." },
          { key: "referral_uptake_pct", label: "Referral uptake", format: pct, title: "% of approved incoming referrals taken up" },
          { key: "claims_per_1000", label: "Claims/1,000" },
          { key: "rating", label: "Rating", format: star },
        ]
        : [
          { key: "enrollees", label: "Enrollees" },
          { key: "encounters", label: "Encounters" },
          { key: "patients_seen", label: "Patients" },
          { key: "codes_requested", label: "Codes" },
          { key: "referrals_out", label: "Ref. out" },
          { key: "referrals_in", label: "Ref. in" },
          { key: "claims_submitted", label: "Claims" },
          { key: "amount_approved", label: "Approved", format: (v) => this.money(v) },
          { key: "rating", label: "Rating", format: star },
        ];
    },
    sortedRows() {
      const k = this.sort;
      const dir = this.direction === "desc" ? -1 : 1;
      return this.rows.slice().sort((a, b) => {
        const x = a[k] === null ? -Infinity : a[k];
        const y = b[k] === null ? -Infinity : b[k];
        return x === y ? 0 : x > y ? dir : -dir;
      });
    },
    trendSeries() {
      return [
        { name: "Encounters", data: this.trend.series.encounters },
        { name: "Authorization codes", data: this.trend.series.authorization_codes },
        { name: "Claims", data: this.trend.series.claims },
      ];
    },
    trendOptions() {
      return {
        chart: { toolbar: { show: true } },
        colors: ["#059669", "#7c3aed", "#db2777"],
        stroke: { width: 3, curve: "smooth" },
        xaxis: { categories: this.trend.categories },
        yaxis: { labels: { formatter: (v) => Math.round(v).toLocaleString() } },
        dataLabels: { enabled: true },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false },
      };
    },
    dxSeries() {
      return [{ name: "Encounters", data: this.diagnoses.diagnoses.map((d) => d.encounters) }];
    },
    dxOptions() {
      const list = this.diagnoses.diagnoses;
      return {
        chart: { toolbar: { show: false }, events: { dataPointSelection: (e, ctx, cfg) => this.drillDiagnosis(list[cfg.dataPointIndex]) } },
        colors: ["#0891b2"],
        plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 3 } },
        xaxis: { categories: list.map((d) => d.name) },
        dataLabels: { enabled: true, formatter: (v, o) => `${v} (${list[o.dataPointIndex].share_pct}%)`, style: { fontSize: "10px" } },
        tooltip: { y: { formatter: (v, o) => `${v} encounters at ${list[o.dataPointIndex].facilities} facilities` } },
      };
    },
  },
  watch: {
    mode() {
      this.metric = this.metricOptions[0].key;
      this.sort = this.metric;
      this.direction = "desc";
    },
  },
  mounted() {
    if (this.agency.state) {
      this.axios.get(`/api/v1/auth/lga/${this.agency.state}`).then((res) => { this.lgas = res.data.data || []; }).catch(() => {});
    }
    if (this.period.from) this.loadAll();
  },
  methods: {
    loadAll() {
      this.loading = true;
      this.dx = null;
      const params = { from: this.period.from, to: this.period.to, lga_id: this.lgaId || undefined, search: this.search || undefined };
      const days = (new Date(this.period.to) - new Date(this.period.from)) / 86400000;
      Promise.all([
        this.$batchGet("/api/v1/auth/analytics/facilities", { params }),
        this.$batchGet("/api/v1/auth/analytics/facilities/trend", { params: { from: this.period.from, to: this.period.to, granularity: days > 120 ? "month" : "week" } }),
        this.$batchGet("/api/v1/auth/analytics/facilities/diagnoses", { params: { from: this.period.from, to: this.period.to, limit: 12 } }),
      ])
        .then(([ranking, trend, dx]) => {
          this.data = ranking.data.data;
          this.trend = trend.data.data;
          this.diagnoses = dx.data.data;
          this.chartKey++;
        })
        .catch((e) => this.$toasted.error((e.response && e.response.data && e.response.data.message) || "Could not load facility performance.", { position: "top-center", duration: 4000 }))
        .finally(() => { this.loading = false; });
    },
    drillDiagnosis(d) {
      if (!d || !d.id) return;
      this.$batchGet("/api/v1/auth/analytics/facilities/diagnoses", { params: { from: this.period.from, to: this.period.to, diagnosis_id: d.id } })
        .then((res) => { this.dx = res.data.data; });
    },
    sortBy(key) {
      if (this.sort === key) this.direction = this.direction === "desc" ? "asc" : "desc";
      else { this.sort = key; this.direction = "desc"; }
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.sortable { cursor: pointer; user-select: none; }
.sortable:hover { color: #1e3a8a !important; }
</style>
